import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload } from 'react-icons/fa';
import { TrainingRequest, TrainingType } from '../types';
import { SearchableSelect } from '../../../utils/select-with-search';
import api from '../../../libs/axios';

interface EditRequestOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  request: TrainingRequest | null;
  trainingTypes: TrainingType[];
  loading?: boolean;
}

export const EditRequestOffcanvas: React.FC<EditRequestOffcanvasProps> = ({
  isOpen, onClose, onSubmit, request, trainingTypes, loading = false,
}) => {
  const [formData, setFormData] = useState({
    candidate_name: '',
    candidate_phone: '',
    candidate_location: '',
    candidate_designation: '',
    designation_id: null as number | null,
    association_type: 'onrole',
    vendor_id: '',
    training_type_id: '',
    remarks: '',
    deduction_applicable: true,
    deduction_period: 'leaving_time',
    deduction_remarks: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [designationOptions, setDesignationOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedDesignation, setSelectedDesignation] = useState<{ value: number; label: string } | null>(null);
  const [designationLoading, setDesignationLoading] = useState(false);

  const [vendorOptions, setVendorOptions] = useState<{ value: number; label: string }[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<{ value: number; label: string } | null>(null);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [requestorDocFiles, setRequestorDocFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setRequestorDocFiles([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (request && isOpen) {
      setFormData({
        candidate_name: request.candidate_name || '',
        candidate_phone: request.candidate_phone || '',
        candidate_location: request.candidate_location || '',
        candidate_designation: request.candidate_designation || '',
        designation_id: request.designation_id ?? null,
        association_type: request.association_type || 'onrole',
        vendor_id: request.vendor_id?.toString() || '',
        training_type_id: request.training_type_id?.toString() || '',
        remarks: (request as any).remarks || '',
        deduction_applicable: request.deduction_applicable,
        deduction_period: request.deduction_period || 'leaving_time',
        deduction_remarks: request.deduction_remarks || '',
      });
      setSelectedDesignation(
        request.designation_id
          ? { value: request.designation_id, label: request.candidate_designation }
          : null,
      );
      if (request.vendor_id && request.vendor) {
        const vLabel = (request.vendor as any).vendor_full_name || (request.vendor as any).vendor_short_name || `Vendor #${request.vendor_id}`;
        setSelectedVendor({ value: request.vendor_id, label: vLabel });
      } else {
        setSelectedVendor(null);
      }
      setErrors({});
    }
  }, [request, isOpen]);

  const fetchDesignations = useCallback(async (search = '') => {
    setDesignationLoading(true);
    try {
      const response = await api.get('/designations/list', {
        params: { search: search.trim() || undefined, limit: 100, status: 'active' },
      });
      if (response.data.success) {
        setDesignationOptions(
          (response.data.data.designations || []).map((d: any) => ({ value: d.id, label: d.name })),
        );
      }
    } finally {
      setDesignationLoading(false);
    }
  }, []);

  const fetchVendors = useCallback(async (search = '') => {
    setVendorLoading(true);
    try {
      const response = await api.get('/vendor/list', {
        params: { search: search.trim() || undefined, status: 'ACTIVE', limit: 50, page: 1 },
      });
      if (response.data.success) {
        setVendorOptions(
          (response.data.data.vendors || []).map((v: any) => ({
            value: v.id,
            label: v.vendor_full_name || v.vendor_short_name || `Vendor #${v.id}`,
          })),
        );
      }
    } finally {
      setVendorLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchDesignations();
      fetchVendors();
    }
  }, [fetchDesignations, fetchVendors, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (formData.designation_id || !formData.candidate_designation || !designationOptions.length) return;
    const match = designationOptions.find(
      o => o.label.toLowerCase() === formData.candidate_designation.toLowerCase(),
    );
    if (match) {
      setSelectedDesignation(match);
      setFormData(prev => ({ ...prev, designation_id: Number(match.value), candidate_designation: match.label }));
    }
  }, [designationOptions, formData.designation_id, formData.candidate_designation, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.candidate_name.trim()) newErrors.candidate_name = 'Required';
    if (!formData.candidate_phone.trim()) newErrors.candidate_phone = 'Required';
    else if (!/^[0-9]{10}$/.test(formData.candidate_phone)) newErrors.candidate_phone = 'Must be 10 digits';
    if (!formData.candidate_location.trim()) newErrors.candidate_location = 'Required';
    if (!formData.designation_id) newErrors.candidate_designation = 'Required';
    if (!formData.training_type_id) newErrors.training_type_id = 'Required';
    if (formData.association_type === 'offrole' && !formData.vendor_id) newErrors.vendor_id = 'Required for off-role';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => {
      const updated = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'association_type' && value !== 'offrole') {
        setSelectedVendor(null);
        updated.vendor_id = '';
      }
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!request || !validateForm()) return;

    const submitData: any = {
      id: request.id,
      candidate_name: formData.candidate_name,
      candidate_phone: formData.candidate_phone,
      candidate_location: formData.candidate_location,
      candidate_designation: formData.candidate_designation,
      designation_id: formData.designation_id,
      association_type: formData.association_type,
      training_type_id: Number(formData.training_type_id),
      deduction_applicable: formData.deduction_applicable,
    };
    if (formData.remarks.trim()) submitData.remarks = formData.remarks.trim();
    if (formData.association_type === 'offrole' && formData.vendor_id) {
      submitData.vendor_id = Number(formData.vendor_id);
    }
    if (formData.deduction_applicable) {
      if (formData.deduction_period) submitData.deduction_period = formData.deduction_period;
      if (formData.deduction_remarks.trim()) submitData.deduction_remarks = formData.deduction_remarks.trim();
    }
    if (requestorDocFiles.length > 0) submitData._requestor_docs = requestorDocFiles;
    onSubmit(submitData);
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen || !request) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-3xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Edit Training Request</h2>
            <p className="text-sm text-gray-600 mt-1">Request ID: {request.request_id}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}>
            <FaTimes className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name *</label>
                <input type="text" name="candidate_name" value={formData.candidate_name} onChange={handleInputChange} disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} />
                {errors.candidate_name && <p className="mt-1 text-sm text-red-600">{errors.candidate_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input type="text" name="candidate_phone" value={formData.candidate_phone} onChange={handleInputChange} disabled={loading} maxLength={10}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_phone ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} />
                {errors.candidate_phone && <p className="mt-1 text-sm text-red-600">{errors.candidate_phone}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trainee State *</label>
                <input type="text" name="candidate_location" value={formData.candidate_location} onChange={handleInputChange} disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.candidate_location ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                  placeholder="Enter trainee state" />
                {errors.candidate_location && <p className="mt-1 text-sm text-red-600">{errors.candidate_location}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                <SearchableSelect
                  value={selectedDesignation}
                  onChange={(option) => {
                    setSelectedDesignation(option ? { value: Number(option.value), label: option.label } : null);
                    setFormData(prev => ({
                      ...prev,
                      designation_id: option ? Number(option.value) : null,
                      candidate_designation: option?.label || '',
                    }));
                    if (errors.candidate_designation) setErrors(prev => ({ ...prev, candidate_designation: '' }));
                  }}
                  options={designationOptions}
                  placeholder="Search and select designation"
                  disabled={loading}
                  isLoading={designationLoading}
                  onSearch={(query: string) => fetchDesignations(query)}
                  minSearchLength={3}
                  error={errors.candidate_designation}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Association Type *</label>
                <select name="association_type" value={formData.association_type} onChange={handleInputChange} disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                  <option value="onrole">On-Role</option>
                  <option value="offrole">Off-Role</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course *</label>
                <select name="training_type_id" value={formData.training_type_id} onChange={handleInputChange} disabled={loading}
                  className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white ${errors.training_type_id ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                  <option value="">Select course</option>
                  {trainingTypes.map(t => <option key={t.id} value={t.id}>{t.training_name}</option>)}
                </select>
                {errors.training_type_id && <p className="mt-1 text-sm text-red-600">{errors.training_type_id}</p>}
              </div>
            </div>

            {formData.association_type === 'offrole' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                <SearchableSelect
                  value={selectedVendor}
                  onChange={(option) => {
                    setSelectedVendor(option ? { value: Number(option.value), label: option.label } : null);
                    setFormData(prev => ({ ...prev, vendor_id: option ? String(option.value) : '' }));
                    if (errors.vendor_id) setErrors(prev => ({ ...prev, vendor_id: '' }));
                  }}
                  options={vendorOptions}
                  placeholder="Search and select vendor"
                  disabled={loading}
                  isLoading={vendorLoading}
                  onSearch={(query: string) => fetchVendors(query)}
                  minSearchLength={3}
                  error={errors.vendor_id}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea name="remarks" value={formData.remarks} onChange={handleInputChange} disabled={loading} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                placeholder="Any additional remarks about this request" />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center gap-3 mb-3">
                <input type="checkbox" id="edit_deduction_applicable" name="deduction_applicable"
                  checked={formData.deduction_applicable} onChange={handleInputChange} className="w-4 h-4 text-blue-600 rounded" />
                <label htmlFor="edit_deduction_applicable" className="text-sm font-medium text-gray-700">Deduction Applicable</label>
              </div>
              {formData.deduction_applicable && (
                <div className="space-y-4 pl-7">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Period</label>
                    <select name="deduction_period" value={formData.deduction_period} onChange={handleInputChange} disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                      <option value="training_time">At Training Time</option>
                      <option value="leaving_time">At Leaving Time</option>
                      <option value="two_months">Within Two Months</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deduction Remarks</label>
                    <textarea name="deduction_remarks" value={formData.deduction_remarks} onChange={handleInputChange} disabled={loading} rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                      placeholder="Remarks about deduction" />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Requestor Documents (optional)</label>
              <label className="flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm border-gray-300 hover:border-blue-400 hover:bg-blue-50">
                <FaUpload className="text-gray-400" />
                <span className="text-gray-500">
                  {requestorDocFiles.length > 0 ? `${requestorDocFiles.length} file(s) selected` : 'Upload documents'}
                </span>
                <input
                  type="file"
                  multiple
                  className="hidden"
                  disabled={loading}
                  onChange={(e) => setRequestorDocFiles(Array.from(e.target.files || []))}
                />
              </label>
              {requestorDocFiles.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {requestorDocFiles.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => setRequestorDocFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="ml-2 text-red-400 hover:text-red-600 flex-shrink-0"
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="button" onClick={() => handleSubmit()} disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {loading
                ? <><FaSpinner className="w-4 h-4 animate-spin" />Updating...</>
                : <><FaSave className="w-4 h-4" />Update Request</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
